import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Facts.css';
import { useLanguage } from '../../../../context/LanguageContext';
import translations from '../../../../translations';

const useCountUp = (end, duration = 2000, shouldStart = false) => {
    const [count, setCount] = useState(0);
    const rafRef = useRef(null);
    const startTimeRef = useRef(null);

    useEffect(() => {
        if (!shouldStart) return;

        const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

        const animate = (timestamp) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp;
            const elapsed = timestamp - startTimeRef.current;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easeOutQuart(progress);

            setCount(Math.floor(easedProgress * end));

            if (progress < 1) {
                rafRef.current = requestAnimationFrame(animate);
            } else {
                setCount(end);
            }
        };

        startTimeRef.current = null;
        rafRef.current = requestAnimationFrame(animate);

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [end, duration, shouldStart]);

    return count;
};

const Facts = () => {
    const { language, direction } = useLanguage();
    const t = translations[language];
    const isRTL = direction === 'rtl';
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    const handleIntersection = useCallback((entries, observer) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
        }
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(handleIntersection, {
            threshold: 0.3,
        });

        const currentRef = sectionRef.current;
        if (currentRef) observer.observe(currentRef);

        return () => {
            if (currentRef) observer.unobserve(currentRef);
        };
    }, [handleIntersection]);

    const employeesCount = useCountUp(3410, 2000, isVisible);
    const projectsCount = useCountUp(856, 2000, isVisible);
    const clientsCount = useCountUp(2250, 2000, isVisible);

    const formatNumber = (num) => num.toLocaleString();

    const facts = [
        {
            id: 1, number: employeesCount, label: t.factsEmployees,
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
            ),
        },
        {
            id: 2, number: projectsCount, label: t.factsProjects,
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21h18"></path>
                    <path d="M5 21V7l8-4v18"></path>
                    <path d="M19 21V11l-6-4"></path>
                    <path d="M9 9h1"></path>
                    <path d="M9 13h1"></path>
                    <path d="M9 17h1"></path>
                </svg>
            ),
        },
        {
            id: 3, number: clientsCount, label: t.factsClients,
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                </svg>
            ),
        },
    ];

    return (
        <section
            className={`facts-section ${isRTL ? 'rtl' : ''}`}
            id="facts"
            ref={sectionRef}
        >
            <div className="facts-container">
                <div className="facts-grid">
                    {facts.map((fact) => (
                        <div key={fact.id} className="fact-item">
                            <div className="fact-icon">{fact.icon}</div>
                            <div className="fact-number">{formatNumber(fact.number)}+</div>
                            <div className="fact-label">{fact.label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Facts;
